// activeBlueprintAsset — which library blueprint (ENG-1030) the editor is
// currently editing, and which one the graph caches belong to.
//
// WHY THIS EXISTS. A character now has several blueprints, but the editor still
// has ONE set of localStorage graph caches (aos-blueprint-v6 / -face-v7 /
// -events-v7, per user and per character). That is deliberate: you edit one
// blueprint at a time, and the caches exist to survive a reload mid-edit, not
// to be a second library. Per-blueprint cache keys would multiply orphaned
// drafts by the size of the library for no benefit.
//
// The danger that arrangement creates is precise: load blueprint A, edit it,
// load blueprint B, and the caches still hold A's graphs. A save would then
// write A's content over B — the exact shape of the two incidents that
// destroyed blueprint content before (myra 2026-08-08, eyeline 2026-08-13; see
// blueprintSaveGuard.js), except the guard cannot catch this one because the
// graphs are populated and plausible.
//
// So the caches are TAGGED with the blueprint they were authored against, and
// a mismatch is a discardable state rather than a merge — the same rule
// characterAuthoringStore applies to a draft authored against another backend.
//
// Pure over an injected storage object so it is testable under node:test.

const KEY_PREFIX = 'aos-blueprint-active'

/**
 * Storage key for one (user, character) pair. Scoped by user for the same
 * reason the graph caches are (ENG-559): two users on one browser must not
 * share editor state.
 */
export function activeKeyFor(userId, charId, graphKind) {
  // Per GRAPH kind (ENG-1030): a row is one graph, so the editor can hold a
  // body graph from one library row and an event graph from another. One tag
  // for all three would claim a single owner that does not exist, and a save
  // would then route two graphs to one row.
  const kind = graphKind ? `-${graphKind}` : ''
  return `${KEY_PREFIX}-${userId ?? 'anon'}-${charId ?? 'none'}${kind}`
}

function storageOr(storage) {
  return storage ?? (typeof localStorage !== 'undefined' ? localStorage : null)
}

function readRecord(key, storage) {
  const store = storageOr(storage)
  if (!store || !key) return null
  try {
    const raw = store.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

/**
 * The blueprint this browser is editing, or null when it has never loaded one
 * by id (the ordinary case for a character whose default was loaded at boot).
 *
 * @returns {{activeAssetId: string|null, cacheAssetId: string|null}}
 */
export function getActiveBlueprint(key, storage) {
  const rec = readRecord(key, storage)
  return {
    activeAssetId: rec?.activeAssetId ?? null,
    cacheAssetId: rec?.cacheAssetId ?? null,
  }
}

/**
 * Record that `assetId` is now loaded AND that the graph caches hold its
 * content. Called right after the graphs are hydrated, never before — the tag
 * has to describe what is actually in the caches.
 */
export function setActiveBlueprint(key, assetId, storage) {
  const store = storageOr(storage)
  if (!store || !key) return
  try {
    store.setItem(key, JSON.stringify({
      activeAssetId: assetId ?? null,
      cacheAssetId: assetId ?? null,
      _savedAt: Date.now(),
    }))
  } catch (e) {
    // A full or blocked storage must not break loading a blueprint; the cost
    // is a lost tag, which resolveCacheOwnership reports as 'unknown'.
    console.warn('[activeBlueprintAsset] could not persist the active blueprint:', e)
  }
}

/** Forget the tag entirely (character switch to one never edited here). */
export function clearActiveBlueprint(key, storage) {
  const store = storageOr(storage)
  if (!store || !key) return
  try { store.removeItem(key) } catch { /* nothing to do */ }
}

/**
 * Whether the graph caches can be trusted for `assetId`.
 *
 * - `'match'`   the caches hold this blueprint — a reload-recovered draft.
 * - `'stale'`   they hold a DIFFERENT blueprint. The caller must discard them
 *               before hydrating, or the next save writes the wrong content
 *               into this blueprint.
 * - `'unknown'` no tag: caches were written before this feature, or by a boot
 *               load of the default, or the tag write failed. Treated as
 *               trustworthy only for the character's default blueprint, which
 *               is what a boot load put there.
 *
 * @param {{activeAssetId: string|null, cacheAssetId: string|null}} record
 * @param {string|null} assetId the blueprint about to be loaded or saved
 */
export function resolveCacheOwnership(record, assetId) {
  const cached = record?.cacheAssetId ?? null
  if (cached == null) return 'unknown'
  if (assetId == null) return 'unknown'
  return cached === assetId ? 'match' : 'stale'
}

/**
 * Whether loading `assetId` must wipe the graph caches first.
 *
 * True only for a proven mismatch. An `unknown` tag is NOT a reason to wipe:
 * a character whose default was hydrated at boot has untagged caches holding
 * exactly the right content, and wiping those would throw away a draft the
 * user has been editing since the page loaded.
 */
export function shouldDiscardCaches(record, assetId) {
  return resolveCacheOwnership(record, assetId) === 'stale'
}
